import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, diasParaVencer } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { useLang, t } from '../../contexts/LangContext';
import ExercicioItem from '../../components/ExercicioItem';
import { dadosCombinacao } from '../../lib/bibliotecaTreinos';
import AlunaTabBar from '../../components/AlunaTabBar';
import RenovacaoBanner from '../../components/RenovacaoBanner';
import PhotoMomentEditor from '../../components/PhotoMomentEditor';
import ExameResultados from '../../components/ExameResultados';
import PlanoAlimentarPreview from '../../components/PlanoAlimentarPreview';
import { supabase } from '../../lib/supabase';
import { normalizarRefeicoes, formatQuantidade, gerarListaDeCompras, gerarReceitas } from '../../lib/dietaUtils';
import { gerarPdfExame, gerarPdfPlanoAlimentar, gerarPdfReceitas } from '../../lib/pdfGenerator';

// Ícones lineares (2px, estilo Lucide/Phosphor) do design system v3,
// usados nas abas e no modal de boas-vindas — substituem os emojis.
const LINE_ICONS = {
  treino: (
    <svg viewBox="0 0 24 24"><path d="M6.5 6.5L17.5 17.5M17.5 6.5L6.5 17.5" /><circle cx="12" cy="12" r="10" /></svg>
  ),
  dieta: (
    <svg viewBox="0 0 24 24"><path d="M12 2v20M2 12h20" /></svg>
  ),
  historico: (
    <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
  ),
  avaliacao: (
    <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="3" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
  ),
  mensagens: (
    <svg viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
  ),
  evolucao: (
    <svg viewBox="0 0 24 24"><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 5-6" /></svg>
  ),
};

const SENSACAO = [
  { value:'facil', label:{ pt:'Fácil', en:'Easy' }, emoji:'😊' },
  { value:'ok', label:{ pt:'Ok', en:'Ok' }, emoji:'😐' },
  { value:'dificil', label:{ pt:'Difícil', en:'Hard' }, emoji:'😓' },
];

function getYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

function horarioParaMinutos(horario = '') {
  const match = String(horario).match(/(\d{1,2})[:h](\d{2})/i);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function proximaRefeicao(refeicoes) {
  if (!refeicoes.length) return null;
  const agora = new Date();
  const minutosAgora = agora.getHours() * 60 + agora.getMinutes();
  return refeicoes.find(ref => {
    const minutos = horarioParaMinutos(ref.horario);
    return minutos !== null && minutos >= minutosAgora;
  }) || refeicoes[0];
}

function treinoComplementar(treino) {
  const texto = `${treino?.titulo || ''} ${treino?.descricao || ''}`.toLowerCase();
  return /bloco|complement|abd[oô]men|panturrilha|core/.test(texto) && !/^treino\s+[a-z]/i.test(treino?.titulo || '');
}

function duracaoEstimada(treino) {
  const quantidade = treino?.exercicios?.length || 0;
  return Math.max(12, Math.min(75, Math.round((quantidade * 3.5) / 5) * 5));
}

// Ícones de linha (2px, estilo Lucide/Phosphor) dos atalhos da Nutrição —
// mesma linguagem visual do LINE_ICONS usado no resto do app.
const NUTRICAO_ICONS = {
  compras: (
    <svg viewBox="0 0 24 24"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" /></svg>
  ),
  observacoes: (
    <svg viewBox="0 0 24 24"><path d="M9 2h6a1 1 0 0 1 1 1v1h1a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1V3a1 1 0 0 1 1-1Z" /><line x1="8" y1="10" x2="16" y2="10" /><line x1="8" y1="14" x2="16" y2="14" /><line x1="8" y1="18" x2="13" y2="18" /></svg>
  ),
  receitas: (
    <svg viewBox="0 0 24 24"><path d="M2 5c2-1 5-1 7 0v14c-2-1-5-1-7 0Z" /><path d="M22 5c-2-1-5-1-7 0v14c2-1 5-1 7 0Z" /></svg>
  ),
  suplementacao: (
    <svg viewBox="0 0 24 24"><path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" /><line x1="8.5" y1="8.5" x2="15.5" y2="15.5" /></svg>
  ),
  exames: (
    <svg viewBox="0 0 24 24"><path d="M9 2v7.5L4.5 18a2 2 0 0 0 1.8 3h11.4a2 2 0 0 0 1.8-3L15 9.5V2" /><line x1="8" y1="2" x2="16" y2="2" /><line x1="8.5" y1="13" x2="15.5" y2="13" /></svg>
  ),
};

// Atalhos da aba Nutrição — cada um abre uma tela cheia (lista de compras,
// observações, receitas, suplementação, exames). Mostra sempre os 5, mas só
// os que já têm conteúdo cadastrado ficam "acesos"; os demais ficam
// esmaecidos dentro do painel escuro, sinalizando que ainda não têm dado.
function NutricaoMenu({ tiles, onSelect }) {
  return (
    <div style={{ background:'linear-gradient(160deg, #2B211D, #1C1613)', borderRadius:'24px', padding:'18px 6px', marginBottom:'20px', border:'1px solid rgba(255,255,255,0.04)' }}>
      <div style={{ display:'flex', gap:'4px', overflowX:'auto', padding:'0 10px', WebkitOverflowScrolling:'touch' }}>
        {tiles.map(tile => (
          <button key={tile.key} onClick={() => tile.ativo && onSelect(tile.key)} disabled={!tile.ativo}
            style={{
              display:'flex', flexDirection:'column', alignItems:'center', gap:'9px',
              minWidth:'72px', flex:'0 0 auto', padding:'2px 6px', background:'transparent', border:'none',
              fontFamily:"'Inter',sans-serif", cursor: tile.ativo ? 'pointer' : 'default',
            }}>
            <span style={{
              width:'46px', height:'46px', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
              background: tile.ativo ? 'var(--accent)' : 'rgba(255,255,255,0.05)',
              boxShadow: tile.ativo ? '0 6px 16px rgba(137,91,64,0.35)' : 'none',
              transition:'transform 0.15s',
            }}>
              {React.cloneElement(NUTRICAO_ICONS[tile.key], { width:'19px', height:'19px', fill:'none', stroke: tile.ativo ? '#FFF6ED' : 'rgba(255,255,255,0.25)', strokeWidth:1.8, strokeLinecap:'round', strokeLinejoin:'round' })}
            </span>
            <span style={{ fontSize:'10.5px', fontWeight:600, textAlign:'center', lineHeight:1.25, color: tile.ativo ? '#FFF6ED' : 'rgba(255,255,255,0.3)' }}>{tile.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// Botão de download de PDF (pedido de exame / plano alimentar / receitas).
function BaixarPdfBtn({ onClick, label }) {
  const [loading, setLoading] = React.useState(false);
  async function handleClick() {
    setLoading(true);
    try { await onClick(); } finally { setLoading(false); }
  }
  return (
    <button onClick={handleClick} disabled={loading}
      style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'7px 14px', background:'transparent', border:'1.5px solid var(--accent)', borderRadius:'100px', color:'var(--accent)', fontSize:'11px', fontWeight:600, cursor: loading?'wait':'pointer', fontFamily:"'Inter',sans-serif", opacity: loading?0.6:1 }}>
      {loading ? '...' : `⬇ ${label}`}
    </button>
  );
}

// Tela cheia acionada por um quadradinho da NutricaoMenu.
function NutricaoFullScreen({ titulo, onBack, children }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'var(--bg)', zIndex:50, display:'flex', flexDirection:'column' }}>
      <div style={{ padding:'16px 20px', display:'flex', alignItems:'center', gap:'12px', borderBottom:'1px solid var(--divider)', background:'var(--surface, #fff)', flexShrink:0 }}>
        <button onClick={onBack} style={{ background:'transparent', border:'none', cursor:'pointer', color:'var(--text)', fontSize:'20px', padding:'4px', lineHeight:1 }}>←</button>
        <div style={{ fontFamily:"'Inter', sans-serif", fontSize:'16px', fontWeight:700, color:'var(--text)' }}>{titulo}</div>
      </div>
      <div style={{ flex:1, overflowY:'auto', padding:'20px' }}>{children}</div>
    </div>
  );
}

// Bloco "Da equipe" colapsável
function ObsColapsavel({ obs, label, id, marginTop, marginBottom }) {
  const [aberto, setAberto] = React.useState(false);
  const frases = obs.split(/(?<=[.!])\s+/).map(s => s.trim()).filter(Boolean);
  const preview = frases[0] || obs;
  const temMais = obs.trim().length > preview.length + 1;
  return (
    <div style={{ padding:'9px 12px', background:'rgba(137,91,64,0.06)', borderLeft:'2px solid var(--accent)', borderRadius:'0 6px 6px 0', marginTop: marginTop||0, marginBottom: marginBottom||0 }}>
      <div style={{ fontSize:'10px', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--accent)', marginBottom:'5px' }}>{label}</div>
      <div style={{ fontSize:'12px', color:'var(--text-3)', lineHeight:1.65 }}>
        {aberto ? obs : preview}
      </div>
      {temMais && (
        <button onClick={e => { e.stopPropagation(); setAberto(a => !a); }}
          style={{ marginTop:'5px', background:'none', border:'none', padding:0, fontSize:'11px', color:'var(--accent)', fontWeight:600, cursor:'pointer', fontFamily:"'Inter',sans-serif", letterSpacing:'0.02em' }}>
          {aberto ? '↑ ver menos' : '↓ ver mais'}
        </button>
      )}
    </div>
  );
}


// Histórico colapsável
function HistoricoColapsavel({ checkins, treinos, lang, formatDate }) {
  const [expandido, setExpandido] = React.useState(null);
  const pt = lang === 'pt';
  const lista = [...checkins].reverse();

  return (
    <>
      {lista.map((ck, idx) => {
        const treino = treinos.find(t => t.id === ck.treinoId);
        const conc = ck.exercicios?.filter(e => e.concluido).length || 0;
        const tot = ck.exercicios?.length || 0;
        const aberto = expandido === ck.id;
        const anterior = lista.slice(idx + 1).find(c => c.treinoId === ck.treinoId);

        return (
          <div key={ck.id} style={{ background:'var(--surface)', border:'1px solid var(--divider)', borderRadius:'20px', marginBottom:'8px', boxShadow:'var(--sh-card)', overflow:'hidden' }}>
            {/* Header clicável */}
            <div onClick={() => setExpandido(a => a === ck.id ? null : ck.id)}
              style={{ padding:'14px 16px', cursor:'pointer', display:'flex', alignItems:'center', gap:'12px' }}>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px' }}>
                  <div style={{ fontSize:'14px', fontWeight:600, color:'var(--text)' }}>{treino?.titulo || 'Treino'}</div>
                  {ck.notaGeral && <span style={{ fontSize:'14px' }}>{ck.notaGeral==='facil'?'😊':ck.notaGeral==='ok'?'😐':'😓'}</span>}
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                  {/* Barra de progresso pequena */}
                  <div style={{ flex:1, height:'3px', background:'var(--divider)', borderRadius:'6px', maxWidth:'80px' }}>
                    <div style={{ height:'100%', width:`${tot>0?(conc/tot)*100:0}%`, background: conc===tot?'#2E7D32':'var(--accent)', borderRadius:'6px' }} />
                  </div>
                  <span style={{ fontSize:'11px', color:'var(--text-2)' }}>{conc}/{tot} {pt?'exercícios':'exercises'}</span>
                  <span style={{ fontSize:'11px', color:'var(--accent)', fontWeight:600 }}>{formatDate(ck.criadoEm)}</span>
                </div>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-2)" strokeWidth="1.5" strokeLinecap="round">
                <polyline points={aberto ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
              </svg>
            </div>

            {/* Detalhe expandido */}
            {aberto && (
              <div style={{ padding:'0 16px 14px', borderTop:'1px solid var(--divider)' }}>
                {ck.exercicios?.map((ex, i) => {
                  const exAnt = anterior?.exercicios?.find(e => e.nome === ex.nome);
                  const ca = ex.carga ? parseFloat(ex.carga) : null;
                  const cb = exAnt?.carga ? parseFloat(exAnt.carga) : null;
                  const diff = (ca && cb) ? ca - cb : null;
                  return (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'7px 0', borderBottom:'1px solid rgba(37,28,25,0.04)', fontSize:'12px', color: ex.pulado?'var(--text-2)':'var(--text-3)', opacity: ex.pulado?0.55:1 }}>
                      <div style={{ width:'16px', height:'16px', borderRadius:'6px', flexShrink:0, background: ex.pulado?'transparent':ex.concluido?'var(--accent)':'transparent', border:`1.5px solid ${ex.pulado?'rgba(37,28,25,0.12)':ex.concluido?'var(--accent)':'var(--divider)'}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'9px', color:'#FFF6ED' }}>
                        {ex.pulado?'–':ex.concluido?'✓':''}
                      </div>
                      <span style={{ flex:1, textDecoration: ex.pulado?'line-through':'none' }}>{ex.nome}</span>
                      {ex.pulado && <span style={{ fontSize:'10px', fontStyle:'italic', color:'var(--text-2)' }}>{pt?'pulado':'skipped'}</span>}
                      {!ex.pulado && ex.carga && <span style={{ color:'var(--text-2)' }}>{ex.carga}</span>}
                      {!ex.pulado && diff !== null && (
                        <span style={{ fontSize:'10px', fontWeight:700, color: diff>0?'#2E7D32':diff<0?'#E53935':'var(--text-2)', background: diff>0?'rgba(46,125,50,0.08)':diff<0?'rgba(229,57,53,0.08)':'transparent', padding:'1px 5px', borderRadius:'100px' }}>
                          {diff>0?`↑ +${diff}kg`:diff<0?`↓ ${diff}kg`:'='}
                        </span>
                      )}
                      {!ex.pulado && ex.sensacao && <span>{ex.sensacao==='facil'?'😊':ex.sensacao==='ok'?'😐':'😓'}</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

export default function AlunaDashboard({ initialTab, previewAlunaId = null, previewTreinoId = null }) {
  const { user: authUser, logout, alunas } = useAuth();
  const previewMode = Boolean(previewAlunaId && authUser?.role === 'admin');
  const previewAluna = previewMode ? (alunas || []).find(a => a.id === previewAlunaId) : null;
  const user = previewAluna || authUser;
  const { treinos, dietas, exames, checkins, feedbacksTreino, salvarCheckin, salvarFeedbackTreino } = useData();
  const { lang, setLang } = useLang();
  const tx = t[lang];
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabInicial = searchParams.get('tab');
  const tabsPermitidas = ['treinos', 'dieta', 'historico', 'avaliacao', 'mensagens'];
  const [tab, setTab] = useState(tabsPermitidas.includes(initialTab) ? initialTab : tabsPermitidas.includes(tabInicial) ? tabInicial : 'treinos');
  const [checkinAtivo, setCheckinAtivo] = useState(null);
  const [checkinData, setCheckinData] = useState({});
  const [checkinSalvo, setCheckinSalvo] = useState(false);
  const [fotoTreino, setFotoTreino] = useState(null);
  const [, setRascunhosAtualizados] = useState(0);
  const [msgTexto, setMsgTexto] = useState('');
  const [mensagens, setMensagens] = useState([]);
  const [msgEnviando, setMsgEnviando] = useState(false);
  const [naoLidas, setNaoLidas] = useState(0);
  const [notaFinal, setNotaFinal] = useState(null);
  const [showBoasVindas, setShowBoasVindas] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourSolicitada, setTourSolicitada] = useState(false);
  const [treinoExpandido, setTreinoExpandido] = useState(null); // id do treino expandido (não ativo)
  const [nutricaoView, setNutricaoView] = useState(null); // 'compras' | 'observacoes' | 'receitas' | 'suplementacao' | 'exames'
  const [mostrarDietasAnteriores, setMostrarDietasAnteriores] = useState(false);
  const [showFeedbackSemana, setShowFeedbackSemana] = useState(false);
  const [salvandoFeedback, setSalvandoFeedback] = useState(false);
  const [feedbackSemana, setFeedbackSemana] = useState({ aparelhosDisponiveis: null, realizouTodos: null, dificuldade: '', sentiuDor: null, substituirExercicio: null, comentario: '' });

  useEffect(() => {
    if (tabsPermitidas.includes(initialTab)) setTab(initialTab);
    else if (tabsPermitidas.includes(tabInicial)) setTab(tabInicial);
  }, [initialTab, tabInicial]); // eslint-disable-line

  // Verificar primeiro acesso
  React.useEffect(() => {
    if (previewMode) return;
    if (!user?.id) return;
    const key = `tcm_tour_acessos_v1_${user.id}`;
    if (!localStorage.getItem(key)) {
      setShowBoasVindas(true);
    }
    supabase.from('tour_requests').select('solicitado_em, concluido_em').eq('aluna_id', user.id).maybeSingle()
      .then(({ data, error }) => {
        if (error && error.code !== '42P01') console.error('Erro ao verificar tour:', error);
        if (data && !data.concluido_em) {
          setTourSolicitada(true);
          setTourStep(0);
          setShowBoasVindas(true);
        }
      });
  }, [user, previewMode]);

  async function fecharBoasVindas() {
    localStorage.setItem(`tcm_tour_acessos_v1_${user.id}`, '1');
    if (tourSolicitada) {
      const { error } = await supabase.from('tour_requests').update({ concluido_em: new Date().toISOString() }).eq('aluna_id', user.id);
      if (error) console.error('Erro ao concluir tour:', error);
      setTourSolicitada(false);
    }
    setShowBoasVindas(false);
    setTourStep(0);
  }

  async function concluirTour() {
    await fecharBoasVindas();
    window.dispatchEvent(new CustomEvent('tcm:show-install-prompt'));
  }
  const [showNotaModal, setShowNotaModal] = useState(false);
  const [treinoParaFinalizar, setTreinoParaFinalizar] = useState(null);
  const msgBottomRef = useRef(null);
  const exercicioRefs = useRef({});

  // Carregar mensagens
  useEffect(() => {
    if (!user?.id) return;
    supabase.from('mensagens').select('*').eq('aluna_id', user.id).order('created_at', { ascending: true })
      .then(({ data }) => setMensagens(data || []));
    supabase.from('mensagens').select('id', { count: 'exact' })
      .eq('aluna_id', user.id).eq('remetente', 'admin').eq('lida', false)
      .then(({ count }) => setNaoLidas(count || 0));
  }, [user]);

  useEffect(() => {
    msgBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens]);

  async function abrirMensagens() {
    setTab('mensagens');
    setNaoLidas(0);
    if (previewMode) return;
    await supabase.from('mensagens').update({ lida: true })
      .eq('aluna_id', user.id).eq('remetente', 'admin').eq('lida', false);
  }

  useEffect(() => {
    if (previewMode || tab !== 'mensagens' || !user?.id) return;
    setNaoLidas(0);
    supabase.from('mensagens').update({ lida: true })
      .eq('aluna_id', user.id).eq('remetente', 'admin').eq('lida', false);
  }, [tab, user, previewMode]);

  async function enviarMensagem(e) {
    e.preventDefault();
    if (previewMode) return;
    if (!msgTexto.trim() || !user?.id) return;
    setMsgEnviando(true);
    const { data } = await supabase.from('mensagens')
      .insert({ aluna_id: user.id, remetente: 'aluna', texto: msgTexto.trim(), lida: false })
      .select().single();
    if (data) setMensagens(m => [...m, data]);
    setMsgTexto('');
    setMsgEnviando(false);
  }

  const formatMsgHora = (iso) => {
    const d = new Date(iso);
    const hoje = new Date();
    if (d.toDateString() === hoje.toDateString()) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const alunaCompleta = (alunas||[]).find(a => a.id === user?.id);
  const diasVencer = diasParaVencer(alunaCompleta?.inicioPlano, alunaCompleta?.plano);
  const todosMeusTreinos = treinos.filter(t => t.alunaId===user?.id).sort((a,b) => (a.ordem||a.semana)-(b.ordem||b.semana));
  const meusTreinos = previewTreinoId ? todosMeusTreinos.filter(t => t.id === previewTreinoId) : todosMeusTreinos;
  const treinosPrincipais = meusTreinos.filter(t => !treinoComplementar(t));
  const treinosComplementares = meusTreinos.filter(treinoComplementar);
  const treinosOrdenados = [...treinosPrincipais, ...treinosComplementares];
  const minhasDietas = dietas.filter(d => d.alunaId===user?.id);
  const meusExames = exames.filter(e => e.alunaId===user?.id);
  const dietaPrincipal = minhasDietas[minhasDietas.length - 1];
  const dietasAnteriores = minhasDietas.slice(0, -1).reverse();
  const refeicoesPrincipal = dietaPrincipal ? normalizarRefeicoes(dietaPrincipal.refeicoes) : [];
  const refeicaoAtualPrincipal = proximaRefeicao(refeicoesPrincipal);
  const listaComprasPrincipal = dietaPrincipal ? (dietaPrincipal.listaCompras?.length > 0 ? dietaPrincipal.listaCompras : gerarListaDeCompras(refeicoesPrincipal)) : [];
  const receitasPrincipal = gerarReceitas(refeicoesPrincipal);
  const orientacoesPrincipal = dietaPrincipal?.orientacoes || [];
  const nutricaoTiles = [
    { key:'compras', label: tx.listaCompras, ativo: listaComprasPrincipal.length > 0 },
    { key:'observacoes', label: lang==='pt'?'Observações':'Notes', ativo: orientacoesPrincipal.length > 0 || !!dietaPrincipal?.obs },
    { key:'receitas', label: lang==='pt'?'Receitas':'Recipes', ativo: receitasPrincipal.length > 0 },
    { key:'suplementacao', label: tx.suplementacao, ativo: dietaPrincipal?.suplementacao?.length > 0 },
    { key:'exames', label: lang==='pt'?'Exames':'Lab tests', ativo: meusExames.length > 0 },
  ];
  const nutricaoTemAlgumAtivo = nutricaoTiles.some(t => t.ativo);
  const meusCheckins = checkins.filter(c => c.alunaId===user?.id);
  const checkinsOrdem = [...meusCheckins].sort((a,b) => new Date(a.criadoEm)-new Date(b.criadoEm));
  const primeiroCheckin = checkinsOrdem[0];
  const cicloChave = primeiroCheckin ? `inicio-${String(primeiroCheckin.criadoEm).slice(0,10)}` : '';
  const feedbackCicloEnviado = feedbacksTreino?.some(f => f.alunaId === user?.id && f.ciclo_chave === cicloChave);
  const diasDesdePrimeiro = primeiroCheckin ? Math.floor((Date.now() - new Date(primeiroCheckin.criadoEm).getTime()) / 86400000) : 0;
  const treinosUnicosFeitos = new Set(meusCheckins.map(c => c.treinoId)).size;
  const revisaoDisponivel = Boolean(primeiroCheckin && !feedbackCicloEnviado && (diasDesdePrimeiro >= 7 || (meusTreinos.length > 0 && treinosUnicosFeitos >= meusTreinos.length)));

  const ultimoCheckin = [...meusCheckins].sort((a,b) => new Date(b.criadoEm)-new Date(a.criadoEm))[0];
  const idxUltimo = treinosPrincipais.findIndex(t => t.id===ultimoCheckin?.treinoId);
  const proximoTreino = treinosPrincipais.length > 0 ? treinosPrincipais[(idxUltimo+1) % treinosPrincipais.length] : null;

  // Progresso semanal
  const hoje = new Date();
  const inicioDaSemana = new Date(hoje);
  inicioDaSemana.setDate(hoje.getDate() - hoje.getDay());
  inicioDaSemana.setHours(0,0,0,0);
  const treinosSemana = meusCheckins.filter(c => new Date(c.criadoEm) >= inicioDaSemana).length;
  const metaSemana = treinosPrincipais.length || meusTreinos.length || 4;

  // Histórico de carga por exercício
  function ultimaCargaExercicio(exNome) {
    for (const ck of [...meusCheckins].sort((a,b) => new Date(b.criadoEm)-new Date(a.criadoEm))) {
      const ex = ck.exercicios?.find(e => e.nome === exNome && e.carga);
      if (ex) return ex.carga;
    }
    return null;
  }

  function iniciarCheckin(treino) {
    const chaveRascunho = `tcm_treino_rascunho_v1_${user.id}_${treino.id}`;
    let rascunho = null;
    if (!previewMode) {
      try { rascunho = JSON.parse(localStorage.getItem(chaveRascunho) || 'null'); } catch (e) {}
    }
    // Retoma o rascunho salvo; se não existir, inicia um objeto limpo.
    const data = {};
    treino.exercicios?.forEach(ex => {
      data[ex.id] = rascunho?.exercicios?.[ex.id] || { concluido:false, marcando:false, carga:'', sensacao:'', obs:'', nome:ex.nome, serieAtual:1 };
    });
    setCheckinData(data);
    setCheckinAtivo(treino.id);
    setCheckinSalvo(false);
  }

  function temRascunho(treinoId) {
    if (previewMode || !user?.id) return false;
    return Boolean(localStorage.getItem(`tcm_treino_rascunho_v1_${user.id}_${treinoId}`));
  }

  function pausarCheckin(treinoId) {
    if (!previewMode && user?.id && Object.keys(checkinData).length > 0) {
      localStorage.setItem(
        `tcm_treino_rascunho_v1_${user.id}_${treinoId}`,
        JSON.stringify({ atualizadoEm:new Date().toISOString(), exercicios:checkinData })
      );
      setRascunhosAtualizados(v => v + 1);
    }
    setCheckinAtivo(null);
  }

  useEffect(() => {
    if (previewMode || !checkinAtivo || !user?.id || Object.keys(checkinData).length === 0) return;
    const chaveRascunho = `tcm_treino_rascunho_v1_${user.id}_${checkinAtivo}`;
    localStorage.setItem(chaveRascunho, JSON.stringify({ atualizadoEm:new Date().toISOString(), exercicios:checkinData }));
  }, [previewMode, checkinAtivo, checkinData, user?.id]);

  useEffect(() => {
    if (!previewMode || !previewTreinoId || checkinAtivo) return;
    const treino = meusTreinos.find(t => t.id === previewTreinoId);
    if (treino) iniciarCheckin(treino);
  }, [previewMode, previewTreinoId, meusTreinos, checkinAtivo]); // eslint-disable-line

  function pedirNotaFinal(treino) {
    setTreinoParaFinalizar(treino);
    setShowNotaModal(true);
  }

  async function confirmarFinalizarCheckin(nota) {
    if (previewMode) {
      setShowNotaModal(false);
      setTreinoParaFinalizar(null);
      return;
    }
    const treino = treinoParaFinalizar;
    const exercicios = Object.entries(checkinData).map(([id,ex]) => ({ id,...ex }));
    await salvarCheckin({ alunaId:user.id, treinoId:treino.id, exercicios, notaGeral: nota });
    localStorage.removeItem(`tcm_treino_rascunho_v1_${user.id}_${treino.id}`);
    setRascunhosAtualizados(v => v + 1);
    setCheckinAtivo(null);
    setCheckinSalvo(true);
    setShowNotaModal(false);
    setNotaFinal(null);
    setTreinoParaFinalizar(null);
    setTimeout(() => setCheckinSalvo(false), 4000);
  }

  async function enviarFeedbackSemana(e) {
    e.preventDefault();
    if (previewMode) return;
    if ([feedbackSemana.aparelhosDisponiveis, feedbackSemana.realizouTodos, feedbackSemana.sentiuDor, feedbackSemana.substituirExercicio].includes(null) || !feedbackSemana.dificuldade) return;
    setSalvandoFeedback(true);
    try {
      await salvarFeedbackTreino({ ...feedbackSemana, alunaId: user.id, cicloChave });
      setShowFeedbackSemana(false);
    } finally {
      setSalvandoFeedback(false);
    }
  }

  // Scroll para próximo exercício não concluído
  function scrollProximo(treinoId) {
    const treino = meusTreinos.find(t => t.id === treinoId);
    if (!treino) return;
    const proximo = treino.exercicios?.find(ex => !checkinData[ex.id]?.concluido);
    if (proximo && exercicioRefs.current[proximo.id]) {
      exercicioRefs.current[proximo.id].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  const concluidos = () => Object.values(checkinData).filter(e => e.concluido).length;
  const totalEx = (treinoId) => meusTreinos.find(t => t.id===treinoId)?.exercicios?.length || 0;
  const formatDate = (iso) => new Date(iso).toLocaleDateString(lang === 'pt' ? 'pt-BR' : 'en-US', { day:'2-digit', month:'2-digit', year:'numeric' });

  const TABS = [
    { id:'treinos', icon:'treino', label: lang==='pt'?'Treino':'Workout' },
    { id:'dieta', icon:'dieta', label: lang==='pt'?'Nutrição':'Nutrition' },
    { id:'historico', icon:'historico', label: lang==='pt'?'Histórico':'History' },
    { id:'avaliacao', icon:'avaliacao', label: lang==='pt'?'Agendar':'Schedule' },
    { id:'mensagens', icon:'mensagens', label: lang==='pt'?'Chat':'Chat' },
  ];

  const pageCopy = {
    treinos: {
      title: lang === 'pt' ? 'Treino' : 'Workout',
      subtitle: lang === 'pt' ? 'Seu plano de movimento em um só lugar.' : 'Your movement plan in one place.',
    },
    dieta: {
      title: lang === 'pt' ? 'Nutrição' : 'Nutrition',
      subtitle: lang === 'pt' ? 'Cardápio, hidratação e orientações da equipe.' : 'Meals, hydration and team guidance.',
    },
    historico: {
      title: lang === 'pt' ? 'Histórico' : 'History',
      subtitle: lang === 'pt' ? 'Registros dos treinos concluídos.' : 'Your completed workout records.',
    },
    avaliacao: {
      title: lang === 'pt' ? 'Agenda' : 'Schedule',
      subtitle: lang === 'pt' ? 'Escolha um horário para sua avaliação.' : 'Choose a time for your assessment.',
    },
    mensagens: {
      title: 'Chat',
      subtitle: lang === 'pt' ? 'Converse com o Team Carol Macedo.' : 'Talk to Team Carol Macedo.',
    },
  };

  const tourSteps = [
    { icon:'evolucao', eyebrow:lang==='pt'?'Bem-vindo ao seu app':'Welcome to your app', title:lang==='pt'?'Vamos fazer um tour rápido?':'Let’s take a quick tour', text:lang==='pt'?'Em menos de um minuto você vai entender onde encontrar cada parte do seu acompanhamento.':'In less than a minute, you’ll know where to find every part of your plan.' },
    { icon:'treino', tab:'treinos', eyebrow:lang==='pt'?'Seus treinos':'Your workouts', title:lang==='pt'?'Treine e registre sua evolução.':'Train and track your progress.', text:lang==='pt'?'Abra sua ficha, veja vídeos e instruções, informe cargas e conte como se sentiu em cada exercício.':'Open your plan, view instructions, log weights and tell us how each exercise felt.' },
    { icon:'dieta', tab:'dieta', eyebrow:lang==='pt'?'Nutrição':'Nutrition', title:lang==='pt'?'Seu plano alimentar sempre à mão.':'Your meal plan at hand.', text:lang==='pt'?'Consulte refeições, substituições, lista de compras, receitas e orientações da equipe.':'See meals, substitutions, shopping lists, recipes and team guidance.' },
    { icon:'historico', tab:'historico', eyebrow:lang==='pt'?'Histórico':'History', title:lang==='pt'?'Acompanhe tudo o que já fez.':'See everything you’ve completed.', text:lang==='pt'?'Aqui ficam os treinos concluídos, cargas utilizadas, sensações e observações registradas.':'Find completed workouts, weights, feelings and saved notes here.' },
    { icon:'evolucao', tab:'avaliacao', eyebrow:lang==='pt'?'Avaliações':'Assessments', title:lang==='pt'?'Agende e acompanhe suas avaliações.':'Schedule and track assessments.', text:lang==='pt'?'Use esta área para marcar avaliações com a equipe e acompanhar sua evolução física.':'Use this area to schedule assessments and follow your physical progress.' },
    { icon:'mensagens', tab:'mensagens', eyebrow:lang==='pt'?'Canal com a equipe':'Team channel', title:lang==='pt'?'Fale diretamente com a Team Carol.':'Talk directly to Team Carol.', text:lang==='pt'?'Envie dúvidas, avise sobre desconfortos ou peça ajustes no treino e na alimentação.':'Ask questions, report discomfort or request workout and nutrition adjustments.' },
    { icon:'evolucao', eyebrow:lang==='pt'?'Acesso rápido':'Quick access', title:lang==='pt'?'Coloque o app na tela inicial.':'Add the app to your home screen.', text:/iphone|ipad|ipod/i.test(window.navigator.userAgent) ? (lang==='pt'?'No Safari, toque em Compartilhar e depois em “Adicionar à Tela de Início”. Assim o app abre em tela cheia, como um aplicativo.':'In Safari, tap Share and then “Add to Home Screen” to open it like an app.') : (lang==='pt'?'Toque em “Instalar” na próxima mensagem para acessar treinos, nutrição e chat direto pela tela inicial.':'Tap “Install” in the next message for quick access from your home screen.') },
  ];
  const tourAtual = tourSteps[tourStep];

  function mudarEtapaTour(proxima) {
    const etapa = tourSteps[proxima];
    if (etapa?.tab) setTab(etapa.tab);
    setTourStep(proxima);
  }

  return (
    <div className="aluna-app-shell" style={{ minHeight:'100vh', background:'#f3eee7', fontFamily:"'Inter',sans-serif" }}>
      <style>{`
        body { background:#fffaf4 !important; }
        .aluna-app-shell { --bg:#fffaf4; --surface:rgba(255,255,255,.78); --surface-2:#f4eee6; --divider:rgba(216,202,182,.72); --text:#251c19; --text-2:#8c7f70; --text-3:#978e86; --accent:#895b40; --btn-text:#fff9f2; --r-btn:999px; --r-card:28px; --sh-card:0 12px 28px rgba(82,69,57,.055), inset 0 1px 0 rgba(255,255,255,.84); background:#fffaf4 !important; padding:26px 14px 40px; color:#1d1d1f; font-family:-apple-system,BlinkMacSystemFont,'SF Pro Display','SF Pro Text','Inter',sans-serif !important; -webkit-font-smoothing:antialiased; }
        .aluna-nav { width:min(100%,430px); margin:0 auto -1px; background:#fffaf4; border:none !important; border-radius:44px 44px 0 0; padding:28px 22px 0 !important; height:auto !important; display:flex; align-items:center; justify-content:space-between; position:relative !important; top:auto !important; z-index:50; box-shadow:0 -18px 80px rgba(66,44,38,.08), inset 0 1px 0 rgba(255,255,255,.95); }
        .aluna-nav img { width:92px !important; height:auto !important; object-fit:contain; }
        .aluna-nav > div { gap:8px !important; }
        .aluna-nav > div > div { background:rgba(239,232,221,.78) !important; border-radius:999px !important; padding:4px !important; box-shadow:inset 0 1px 0 rgba(255,255,255,.62); }
        .aluna-nav > div > div button { min-width:40px !important; height:34px !important; padding:0 !important; border-radius:999px !important; font-size:13px !important; color:#8c7f70 !important; }
        .aluna-nav > div > div button[style*="--surface"] { background:rgba(255,255,255,.86) !important; color:#422c26 !important; box-shadow:0 6px 18px rgba(66,44,38,.08); }
        .aluna-nav > div > button:nth-of-type(1) { height:42px !important; border:1px solid rgba(216,202,182,.86) !important; border-radius:999px !important; background:rgba(255,255,255,.54) !important; color:#7f7165 !important; padding:0 14px !important; font-size:13px !important; }
        .aluna-nav > div > button:nth-of-type(2) { display:none !important; }
        .aluna-content { width:min(100%,430px); min-height:calc(100vh - 56px); margin:0 auto; padding:24px 22px 118px !important; background:#fffaf4; border-radius:0 0 44px 44px; box-shadow:0 28px 80px rgba(66,44,38,.10), inset 0 -1px 0 rgba(255,255,255,.95); }
        .aluna-content > h1 { color:#251c19 !important; font-size:22px !important; line-height:1.16 !important; font-weight:600 !important; letter-spacing:0 !important; margin:0 0 4px !important; }
        .aluna-content > h1 em { color:#251c19 !important; font-style:normal !important; }
        .aluna-content > p { color:#968d84 !important; font-size:12px !important; margin-bottom:22px !important; }
        .aluna-tabs { display:none !important; }
        .aluna-tab { flex:1; padding:8px 4px; border:none; background:transparent; font-size:11px; font-weight:500; color:var(--text-2); cursor:pointer; border-radius:var(--r-btn); transition:all 0.2s; font-family:'Inter',sans-serif; display:flex; flex-direction:column; align-items:center; gap:2px; }
        .aluna-tab.active { background:var(--text); color:#FAF7F2; }
        .aluna-tab-icon { display:flex; }
        .aluna-tab-icon svg { width:16px; height:16px; stroke:currentColor; stroke-width:2; fill:none; stroke-linecap:round; stroke-linejoin:round; }
        .treino-card { background:rgba(255,255,255,.82) !important; border:1px solid rgba(255,255,255,.78) !important; border-radius:30px !important; padding:16px !important; margin-bottom:14px !important; box-shadow:0 14px 34px rgba(82,69,57,.06), inset 0 1px 0 rgba(255,255,255,.86) !important; overflow:hidden; }
        .treino-card div[style*="font-size:'18px'"], .treino-card div[style*="font-size:'16px'"] { color:#251c19 !important; font-weight:600 !important; letter-spacing:0 !important; }
        .workout-progress-card { background:linear-gradient(180deg, rgba(255,255,255,.86), rgba(255,250,244,.78)) !important; border:1px solid rgba(255,255,255,.82) !important; border-radius:28px !important; padding:16px !important; margin-bottom:14px !important; box-shadow:0 14px 34px rgba(82,69,57,.06), inset 0 1px 0 rgba(255,255,255,.86) !important; }
        .workout-next-card { background:linear-gradient(135deg, rgba(86,82,76,.96), rgba(37,28,25,.98)) !important; border:1px solid rgba(255,255,255,.22) !important; border-radius:28px !important; box-shadow:0 20px 42px rgba(37,28,25,.16), inset 0 1px 0 rgba(255,255,255,.15) !important; }
        .workout-preview-ex { display:grid !important; grid-template-columns:42px minmax(0, 1fr) !important; align-items:center !important; gap:12px !important; padding:13px 14px !important; border-radius:24px !important; background:rgba(255,255,255,.72) !important; border:1px solid rgba(255,255,255,.72) !important; box-shadow:0 10px 24px rgba(82,69,57,.045), inset 0 1px 0 rgba(255,255,255,.84) !important; }
        .workout-preview-ex .last-load { grid-column:2; justify-self:start; max-width:100%; white-space:normal; line-height:1.25; margin-top:-4px; }
        .workout-card-head { border-radius:28px; transition:background .18s ease; }
        .workout-card-head:active { background:rgba(244,238,230,.58); }
        .empty-state { padding:20px; text-align:left; color:#8c7f70; font-size:13px; background:rgba(255,255,255,.78); border:1px solid rgba(255,255,255,.72); border-radius:28px; line-height:1.6; box-shadow:0 12px 28px rgba(82,69,57,.055); }
        .aluna-page-head { margin-bottom:20px; }
        .aluna-page-head span { display:block; color:#895b40; font-size:11px; font-weight:600; letter-spacing:.09em; text-transform:uppercase; margin-bottom:8px; }
        .aluna-page-head h1 { margin:0; color:#251c19; font-size:30px; line-height:1.08; font-weight:650; letter-spacing:0; }
        .aluna-page-head p { margin:8px 0 0; color:#8c7f70; font-size:13px; line-height:1.5; }
        .nutrition-summary { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:14px; }
        .nutrition-chip { min-height:74px; border-radius:24px; padding:14px; background:rgba(255,255,255,.78); border:1px solid rgba(255,255,255,.72); box-shadow:0 12px 28px rgba(82,69,57,.055), inset 0 1px 0 rgba(255,255,255,.84); }
        .nutrition-chip small { display:block; color:#8c7f70; font-size:11px; margin-bottom:4px; }
        .nutrition-chip strong { display:block; color:#251c19; font-size:16px; line-height:1.25; font-weight:600; }
        .nutrition-actions { display:flex; gap:10px; margin:0 -22px 16px; padding:0 22px 4px; overflow-x:auto; -webkit-overflow-scrolling:touch; scrollbar-width:none; }
        .nutrition-actions::-webkit-scrollbar { display:none; }
        .nutrition-action { min-width:112px; min-height:96px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; padding:12px 10px; border:0; border-radius:26px; background:rgba(255,255,255,.78); box-shadow:0 12px 28px rgba(82,69,57,.055), inset 0 1px 0 rgba(255,255,255,.84); color:#251c19; text-align:center; font-family:inherit; flex:0 0 auto; cursor:pointer; }
        .nutrition-action i { width:42px; height:42px; border-radius:18px; display:grid; place-items:center; background:#d9ddc1; color:#5e6840; font-style:normal; }
        .nutrition-action strong { display:block; font-size:13px; font-weight:600; line-height:1.16; }
        .nutrition-action small, .nutrition-action em { display:none; }
        .section-lbl { font-size:11px; font-weight:600; letter-spacing:.09em; text-transform:uppercase; color:#895b40; margin-bottom:10px; display:flex; align-items:center; gap:8px; }
        .section-line { flex:1; height:1px; background:rgba(140,127,112,.18); }
        .aluna-chat { min-height:calc(100dvh - 292px); display:grid; grid-template-rows:auto 1fr auto; gap:12px; padding-bottom:172px; }
        .aluna-chat-hero { display:grid; grid-template-columns:48px 1fr; gap:14px; align-items:center; border-radius:30px; padding:18px; position:relative; overflow:hidden; background:rgba(45,42,39,.72); border:1px solid rgba(255,255,255,.18); box-shadow:0 18px 42px rgba(37,28,25,.18), inset 0 1px 0 rgba(255,255,255,.16); }
        .aluna-chat-hero:before { content:""; position:absolute; inset:0; background:radial-gradient(circle at 16% 8%, rgba(140,127,112,.42), transparent 38%), linear-gradient(135deg, rgba(68,64,60,.9), rgba(34,31,29,.78)); z-index:0; }
        .aluna-chat-avatar { width:48px; height:48px; border-radius:50%; display:grid; place-items:center; background:rgba(255,255,255,.14); color:#f7efe5; font-size:12px; font-weight:600; position:relative; z-index:1; }
        .aluna-chat-hero div:last-child { position:relative; z-index:1; }
        .aluna-chat-hero span { display:block; color:#cfc5b7; font-size:11px; font-weight:500; margin-bottom:4px; }
        .aluna-chat-hero strong { display:block; color:#fff9f2; font-size:18px; line-height:1.18; font-weight:600; letter-spacing:0; }
        .aluna-chat-hero p { margin:7px 0 0; color:rgba(255,249,242,.72); font-size:12px; line-height:1.42; }
        .aluna-chat-thread { min-height:180px; overflow-y:auto; display:grid; align-content:start; gap:10px; padding:4px 0 20px; }
        .aluna-message { width:fit-content; max-width:86%; border-radius:24px; padding:12px 14px; box-shadow:0 10px 24px rgba(82,69,57,.055); }
        .aluna-message small { display:block; color:#8c7f70; font-size:11px; font-weight:600; margin-bottom:4px; }
        .aluna-message p { margin:0; font-size:13px; line-height:1.42; white-space:pre-wrap; }
        .aluna-message time { display:block; margin-top:5px; font-size:10px; opacity:.5; text-align:right; }
        .aluna-message.is-team { justify-self:start; background:rgba(255,255,255,.8); color:#342a25; border:1px solid rgba(255,255,255,.72); }
        .aluna-message.is-user { justify-self:end; background:#dce4cf; color:#34402a; }
        .aluna-chat-empty { align-self:center; color:#8c7f70; font-size:13px; line-height:1.6; text-align:center; padding:36px 16px; }
        .aluna-chat-composer { min-height:58px; display:grid; grid-template-columns:1fr 44px; gap:10px; align-items:center; border-radius:999px; padding:8px 10px 8px 16px; background:rgba(255,255,255,.94); border:1px solid rgba(255,255,255,.88); box-shadow:0 18px 38px rgba(82,69,57,.12); position:fixed; left:50%; bottom:max(104px, calc(env(safe-area-inset-bottom, 0px) + 104px)); width:min(386px, calc(100% - 44px)); transform:translateX(-50%); z-index:180; backdrop-filter:blur(18px); -webkit-backdrop-filter:blur(18px); }
        .aluna-chat-composer textarea { border:0 !important; background:transparent !important; padding:10px 0 !important; color:#342a25 !important; font-size:13px !important; resize:none !important; outline:none !important; font-family:inherit !important; }
        .aluna-chat-composer button { width:44px; height:44px; border:0; border-radius:50%; display:flex; align-items:center; justify-content:center; background:#251c19; color:#fff9f2; font-size:0; cursor:pointer; }
        .aluna-chat-composer button:after { content:""; width:18px; height:18px; display:block; transform:translateX(1px); background:currentColor; clip-path:polygon(2% 5%, 100% 50%, 2% 95%, 20% 56%, 54% 50%, 20% 44%); }
        .nota-modal-overlay { position:fixed; inset:0; background:rgba(37,28,25,0.6); display:flex; align-items:flex-end; justify-content:center; z-index:200; padding:20px; }
        .nota-modal { background:#FAF7F2; border-radius:24px 24px 0 0; padding:32px 24px; width:100%; max-width:480px; }
        @media(max-width:560px){
          body, #root { background:#fffaf4 !important; }
          .aluna-app-shell{ padding:0; background:#fffaf4 !important; overflow-x:hidden; }
          .aluna-nav{ width:100%; border-radius:0; padding:calc(env(safe-area-inset-top, 0px) + 14px) 16px 0 !important; box-shadow:none; gap:12px; background:#fffaf4 !important; }
          .aluna-content{ width:100%; min-height:calc(100dvh - 72px); border-radius:0; padding:22px 16px calc(132px + env(safe-area-inset-bottom, 0px)) !important; box-shadow:none; overflow-x:hidden; background:#fffaf4 !important; }
          .aluna-chat{ min-height:calc(100dvh - 246px); padding-bottom:188px; }
          .aluna-chat-composer{ width:calc(100% - 36px); bottom:max(104px, calc(env(safe-area-inset-bottom, 0px) + 104px)); }
          .aluna-nav img{ width:84px !important; }
          .aluna-nav > div{ gap:6px !important; }
          .aluna-nav > div > div button{ min-width:36px !important; height:32px !important; font-size:12px !important; }
          .aluna-nav > div > button:nth-of-type(1){ height:40px !important; padding:0 12px !important; }
          .aluna-page-head h1{ font-size:28px; }
          .workout-preview-ex { grid-template-columns:38px minmax(0, 1fr) !important; padding:12px !important; }
          .workout-preview-ex > div:first-child { width:38px !important; height:38px !important; border-radius:16px !important; }
          .treino-card { border-radius:28px !important; }
          .nutrition-summary{ gap:8px; }
          .nutrition-chip{ min-height:68px; padding:12px; border-radius:22px; }
          .nutrition-action{ min-width:104px; min-height:90px; border-radius:24px; }
        }
      `}</style>

      {/* Tour guiada do primeiro acesso */}
      {showBoasVindas && (
        <div style={{ position:'fixed', inset:0, background:'rgba(24,18,16,0.72)', backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:300, padding:'16px' }}>
          <div role="dialog" aria-modal="true" aria-label={tourAtual.title} style={{ position:'relative', background:'#fffaf4', border:'1px solid rgba(255,255,255,.76)', borderRadius:'32px', padding:'28px 24px 22px', maxWidth:'430px', width:'100%', textAlign:'center', boxShadow:'0 30px 90px rgba(24,18,16,.38)' }}>
            <button onClick={fecharBoasVindas} aria-label={lang==='pt'?'Fechar tour':'Close tour'} style={{ position:'absolute', top:'14px', right:'14px', width:'34px', height:'34px', border:0, borderRadius:'50%', background:'rgba(37,28,25,.06)', color:'#8c7f70', fontSize:'20px' }}>×</button>
            <div style={{ width:'64px', height:'64px', borderRadius:'24px', display:'grid', placeItems:'center', margin:'4px auto 20px', color:'#895b40', background:'#efe2d7' }}>
              {React.cloneElement(LINE_ICONS[tourAtual.icon] || LINE_ICONS.evolucao, { style:{ width:'28px', height:'28px', stroke:'currentColor', strokeWidth:1.8, fill:'none' } })}
            </div>
            <div style={{ color:'#895b40', fontSize:'10px', fontWeight:700, letterSpacing:'.16em', textTransform:'uppercase', marginBottom:'10px' }}>{tourAtual.eyebrow}</div>
            <h2 style={{ fontSize:'25px', fontWeight:680, color:'#251c19', margin:'0 auto 10px', lineHeight:1.14, maxWidth:'340px' }}>{tourAtual.title}</h2>
            <p style={{ fontSize:'14px', color:'#8c7f70', lineHeight:1.6, margin:'0 auto 24px', maxWidth:'350px', minHeight:'68px' }}>{tourAtual.text}</p>
            <div aria-label={`${tourStep + 1} de ${tourSteps.length}`} style={{ display:'flex', justifyContent:'center', gap:'7px', marginBottom:'22px' }}>
              {tourSteps.map((_,i) => <span key={i} style={{ width:i===tourStep?'28px':'7px', height:'7px', borderRadius:'99px', background:i===tourStep?'#895b40':'#ded4c8', transition:'all .2s' }} />)}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr auto auto', alignItems:'center', gap:'8px' }}>
              <button onClick={fecharBoasVindas} style={{ justifySelf:'start', padding:'10px 2px', border:0, background:'transparent', color:'#8c7f70', fontSize:'12px', fontWeight:600 }}>{lang==='pt'?'Pular tour':'Skip tour'}</button>
              {tourStep > 0 && <button onClick={() => mudarEtapaTour(tourStep-1)} aria-label={lang==='pt'?'Voltar':'Back'} style={{ width:'44px', height:'44px', border:'1.5px solid #ded4c8', borderRadius:'16px', background:'transparent', color:'#251c19', fontSize:'22px' }}>‹</button>}
              <button onClick={() => tourStep === tourSteps.length-1 ? concluirTour() : mudarEtapaTour(tourStep+1)} style={{ minWidth:'112px', height:'44px', padding:'0 18px', border:0, borderRadius:'16px', background:'#251c19', color:'#fff', fontSize:'13px', fontWeight:700 }}>{tourStep === tourSteps.length-1 ? (lang==='pt'?'Instalar app →':'Install app →') : (lang==='pt'?'Próximo ›':'Next ›')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal nota final */}
      {showNotaModal && (
        <div className="nota-modal-overlay" onClick={() => setShowNotaModal(false)}>
          <div className="nota-modal" onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily:"'Inter', sans-serif", fontSize:'20px', fontWeight:700, color:'var(--text)', marginBottom:'6px', textAlign:'center' }}>
              {lang==='pt' ? 'Como foi o treino?' : 'How was your workout?'}
            </div>
            <div style={{ fontSize:'13px', color:'var(--text-2)', textAlign:'center', marginBottom:'24px' }}>
              {lang==='pt' ? 'Registre sua sensação geral hoje.' : 'Rate your overall feeling today.'}
            </div>
            <div style={{ display:'flex', gap:'10px', marginBottom:'20px' }}>
              {SENSACAO.map(op => (
                <button key={op.value} onClick={() => setNotaFinal(op.value)}
                  style={{ flex:1, padding:'16px 8px', border:`2px solid ${notaFinal===op.value?'var(--text)':'var(--divider)'}`, borderRadius:'20px', background: notaFinal===op.value?'var(--text)':'transparent', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:'6px', transition:'all 0.15s' }}>
                  <span style={{ fontSize:'24px' }}>{op.emoji}</span>
                  <span style={{ fontSize:'12px', fontWeight:600, color: notaFinal===op.value?'#FAF7F2':'var(--text)' }}>{op.label[lang] || op.label.pt}</span>
                </button>
              ))}
            </div>
            <button onClick={() => confirmarFinalizarCheckin(notaFinal)}
              style={{ width:'100%', padding:'14px', background: notaFinal?'#2E7D32':'rgba(37,28,25,0.12)', color: notaFinal?'#fff':'var(--text-2)', border:'none', borderRadius:'20px', fontSize:'14px', fontWeight:700, letterSpacing:'0.04em', textTransform:'uppercase', cursor: notaFinal?'pointer':'not-allowed', fontFamily:"'Inter',sans-serif" }}>
              {lang==='pt' ? 'Finalizar treino ✓' : 'Finish workout ✓'}
            </button>
            <button onClick={() => confirmarFinalizarCheckin(null)}
              style={{ width:'100%', marginTop:'10px', padding:'10px', background:'transparent', border:'none', color:'var(--text-2)', fontSize:'12px', cursor:'pointer' }}>
              {lang==='pt' ? 'Pular' : 'Skip'}
            </button>
          </div>
        </div>
      )}

      {showFeedbackSemana && (
        <div className="nota-modal-overlay" onClick={() => setShowFeedbackSemana(false)}>
          <form className="nota-modal" onSubmit={enviarFeedbackSemana} onClick={e => e.stopPropagation()} style={{ maxHeight:'88dvh', overflowY:'auto' }}>
            <div style={{ fontSize:'20px', fontWeight:700, color:'var(--text)', marginBottom:'6px', textAlign:'center' }}>Revisão da primeira semana</div>
            <div style={{ fontSize:'13px', color:'var(--text-2)', textAlign:'center', marginBottom:'22px', lineHeight:1.5 }}>Suas respostas vão direto para a equipe ajustar o treino, se necessário.</div>
            {[
              ['aparelhosDisponiveis','Encontrou todos os aparelhos?'],
              ['realizouTodos','Conseguiu fazer todos os exercícios?'],
              ['sentiuDor','Sentiu dor ou desconforto?'],
              ['substituirExercicio','Acha que algum exercício precisa ser substituído?'],
            ].map(([campo, pergunta]) => (
              <div key={campo} style={{ marginBottom:'16px' }}>
                <div style={{ fontSize:'13px', fontWeight:650, color:'var(--text)', marginBottom:'8px' }}>{pergunta}</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                  {[[true,'Sim'],[false,'Não']].map(([valor,label]) => <button key={label} type="button" onClick={() => setFeedbackSemana(f => ({...f,[campo]:valor}))} style={{ padding:'10px', border:`1.5px solid ${feedbackSemana[campo]===valor?'var(--text)':'var(--divider)'}`, borderRadius:'16px', background:feedbackSemana[campo]===valor?'var(--text)':'transparent', color:feedbackSemana[campo]===valor?'#fff':'var(--text)', fontWeight:600 }}>{label}</button>)}
                </div>
              </div>
            ))}
            <div style={{ marginBottom:'16px' }}>
              <div style={{ fontSize:'13px', fontWeight:650, color:'var(--text)', marginBottom:'8px' }}>Como ficou a dificuldade geral?</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'7px' }}>
                {[['facil','Fácil'],['adequada','Adequada'],['dificil','Difícil']].map(([valor,label]) => <button key={valor} type="button" onClick={() => setFeedbackSemana(f => ({...f,dificuldade:valor}))} style={{ padding:'10px 4px', border:`1.5px solid ${feedbackSemana.dificuldade===valor?'var(--text)':'var(--divider)'}`, borderRadius:'16px', background:feedbackSemana.dificuldade===valor?'var(--text)':'transparent', color:feedbackSemana.dificuldade===valor?'#fff':'var(--text)', fontSize:'12px', fontWeight:600 }}>{label}</button>)}
              </div>
            </div>
            <label style={{ display:'block', fontSize:'13px', fontWeight:650, color:'var(--text)', marginBottom:'8px' }}>Quer contar mais alguma coisa? <span style={{ fontWeight:400, color:'var(--text-2)' }}>(opcional)</span></label>
            <textarea value={feedbackSemana.comentario} onChange={e => setFeedbackSemana(f => ({...f,comentario:e.target.value}))} placeholder="Ex.: não tinha cadeira extensora, senti desconforto no joelho..." rows={3} style={{ width:'100%', padding:'12px', border:'1.5px solid var(--divider)', borderRadius:'16px', resize:'vertical', fontFamily:'Inter, sans-serif', fontSize:'13px', marginBottom:'16px' }} />
            <button type="submit" disabled={salvandoFeedback} style={{ width:'100%', padding:'13px', border:0, borderRadius:'18px', background:'var(--text)', color:'#fff', fontSize:'13px', fontWeight:700, opacity:salvandoFeedback?.6:1 }}>{salvandoFeedback?'Enviando...':'Enviar revisão ✓'}</button>
          </form>
        </div>
      )}

      <nav className="aluna-nav">
        <img src="/logo.svg" alt="Team Carol Macedo" style={{ height:'30px', width:'auto', objectFit:'contain' }} />
        <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
          <div style={{ display:'flex', gap:'2px', background:'var(--surface-2, var(--surface-2))', borderRadius:'var(--r-btn)', padding:'2px' }}>
            <button onClick={() => setLang('pt')} style={{ padding:'4px 8px', borderRadius:'var(--r-btn)', border:'none', background: lang==='pt'?'var(--surface, #fff)':'transparent', color: lang==='pt'?'var(--text)':'var(--text-2)', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>PT</button>
            <button onClick={() => setLang('en')} style={{ padding:'4px 8px', borderRadius:'var(--r-btn)', border:'none', background: lang==='en'?'var(--surface, #fff)':'transparent', color: lang==='en'?'var(--text)':'var(--text-2)', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>EN</button>
          </div>
          {previewMode ? (
            <button onClick={() => navigate(-1)} style={{ padding:'6px 14px', background:'var(--text)', border:'1px solid var(--text)', color:'#fff', borderRadius:'var(--r-btn)', fontSize:'11px', cursor:'pointer' }}>← Voltar ao admin</button>
          ) : <>
            <button onClick={() => navigate('/aluna/perfil')} style={{ background:'transparent', border:'none', color:'var(--text-2)', fontSize:'12px', cursor:'pointer' }}>{tx.perfil}</button>
            <button onClick={logout} style={{ padding:'6px 14px', background:'transparent', border:'1px solid var(--divider)', color:'var(--text-2)', borderRadius:'var(--r-btn)', fontSize:'11px', cursor:'pointer' }}>{tx.sair}</button>
          </>}
        </div>
      </nav>

      <div className="aluna-content">
        {previewMode && (
          <div style={{ position:'sticky', top:8, zIndex:40, marginBottom:14, padding:'10px 14px', borderRadius:16, background:'#6B4FA8', color:'#fff', boxShadow:'0 8px 24px rgba(58,39,99,.22)', fontSize:12, lineHeight:1.45 }}>
            <strong>Visão da aluna: {user?.name}</strong><br />Prévia segura — nenhuma conclusão, mensagem ou alteração será salva.
          </div>
        )}
        <RenovacaoBanner dias={diasVencer} lang={lang} />
        <div className="aluna-page-head">
          <span>Team Carol Macedo</span>
          <h1>{pageCopy[tab]?.title}</h1>
          <p>{pageCopy[tab]?.subtitle}</p>
        </div>

        {checkinSalvo && (
          <div style={{ padding:'11px 14px', background:'rgba(76,175,80,0.07)', border:'1px solid rgba(76,175,80,0.25)', color:'#2E7D32', borderRadius:'var(--r-btn)', fontSize:'13px', marginBottom:'16px' }}>
            ✓ {lang==='pt'?'Treino salvo! Ótimo trabalho':'Workout saved! Great job'}, {user?.name?.split(' ')[0]}.
            {proximoTreino && <span style={{ marginLeft:'6px' }}>{tx.proximo} <strong>{proximoTreino.titulo}</strong></span>}
          </div>
        )}

        {fotoTreino && <PhotoMomentEditor userId={user?.id} treino={fotoTreino} onClose={() => setFotoTreino(null)} onPublished={() => setFotoTreino(null)} />}

        {/* ABAS COM ÍCONE */}
        <div className="aluna-tabs">
          {TABS.map(({ id, icon, label }) => (
            <button key={id} className={`aluna-tab${tab===id?' active':''}`}
              onClick={() => id === 'mensagens' ? abrirMensagens() : setTab(id)}
              style={{ position: 'relative' }}>
              <span className="aluna-tab-icon">{LINE_ICONS[icon]}</span>
              <span>{label}</span>
              {id === 'mensagens' && naoLidas > 0 && (
                <span style={{ position:'absolute', top:'2px', right:'2px', background:'#5B402E', color:'white', borderRadius:'20px', padding:'0 5px', fontSize:'9px', fontWeight:700, lineHeight:'16px' }}>{naoLidas}</span>
              )}
              {id === 'treinos' && checkinAtivo && (
                <span style={{ position:'absolute', top:'2px', right:'2px', width:'8px', height:'8px', background:'#2E7D32', borderRadius:'50%' }} />
              )}
            </button>
          ))}
        </div>

        {/* TREINOS */}
        {tab==='treinos' && (
          user?.acessoTreino === false
            ? <div className="empty-state">🔒 {lang==='pt' ? 'Treino não faz parte do seu plano atual. Fale com a equipe para adicionar.' : 'Workouts are not included in your current plan. Contact the team to add it.'}</div>
            : meusTreinos.length===0
            ? <div className="empty-state">{tx.noTreino}</div>
            : <>
              {revisaoDisponivel && !checkinAtivo && (
                <div style={{ padding:'18px', marginBottom:'14px', background:'linear-gradient(135deg, #251C19, #5B402E)', color:'#fff', borderRadius:'24px', boxShadow:'var(--sh-card)' }}>
                  <div style={{ fontSize:'10px', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'#E8D5C7', marginBottom:'5px' }}>Revisão do ciclo</div>
                  <div style={{ fontSize:'16px', fontWeight:700, marginBottom:'5px' }}>Como foi sua primeira semana?</div>
                  <div style={{ fontSize:'12px', lineHeight:1.5, color:'#E3DBC7', marginBottom:'14px' }}>Conte se encontrou os aparelhos e se a dificuldade ficou adequada. Sua resposta ajuda a equipe a manter ou ajustar sua ficha.</div>
                  <button type="button" onClick={() => setShowFeedbackSemana(true)} style={{ width:'100%', padding:'11px', border:0, borderRadius:'18px', background:'#fff', color:'#251C19', fontSize:'13px', fontWeight:700 }}>Responder agora →</button>
                </div>
              )}
              {!checkinAtivo && (
                <div className="workout-progress-card">
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                    <div>
                      <div style={{ fontSize:'10px', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--accent)', marginBottom:'2px' }}>
                        {lang==='pt' ? 'Semana atual' : 'This week'}
                      </div>
                      <div style={{ fontSize:'15px', fontWeight:700, color:'var(--text)' }}>
                        {treinosSemana} {lang==='pt' ? 'de' : 'of'} {metaSemana} {lang==='pt' ? 'treinos' : 'workouts'}
                      </div>
                    </div>
                    <div style={{ fontFamily:"'Inter', sans-serif", fontSize:'28px', fontWeight:700, color: treinosSemana >= metaSemana ? '#2E7D32' : 'var(--accent)' }}>
                      {treinosSemana >= metaSemana ? '🏆' : `${Math.round((treinosSemana/metaSemana)*100)}%`}
                    </div>
                  </div>
                  <div style={{ height:'6px', background:'var(--surface-2)', borderRadius:'6px', overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${Math.min((treinosSemana/metaSemana)*100, 100)}%`, background: treinosSemana >= metaSemana ? '#2E7D32' : 'var(--accent)', borderRadius:'6px', transition:'width 0.5s ease' }} />
                  </div>
                  {treinosSemana >= metaSemana && (
                    <div style={{ fontSize:'12px', color:'#2E7D32', marginTop:'8px', fontWeight:600 }}>
                      ✓ {lang==='pt' ? 'Meta da semana concluída! Incrível.' : 'Weekly goal complete! Amazing.'}
                    </div>
                  )}
                </div>
              )}
              {proximoTreino && !checkinAtivo && (
                <div className="workout-next-card" style={{ padding:'20px 22px', marginBottom:'16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px', flexWrap:'wrap' }}>
                  <div>
                    <div style={{ fontSize:'10px', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'#E4D3B8', marginBottom:'3px' }}>{lang==='pt' ? 'Seu próximo passo' : 'Your next step'}</div>
                    <div style={{ fontSize:'15px', fontWeight:600, color:'#fff' }}>{proximoTreino.titulo}</div>
                    <div style={{ fontSize:'12px', color:'#D9C9B3', marginTop:'3px' }}>{proximoTreino.descricao} · {duracaoEstimada(proximoTreino)} min · {proximoTreino.exercicios?.length || 0} {lang==='pt'?'exercícios':'exercises'}</div>
                  </div>
                  <button onClick={() => iniciarCheckin(proximoTreino)} style={{ padding:'9px 18px', background:'#fff', color:'var(--accent-hover, var(--accent-hover))', border:'none', borderRadius:'20px', fontSize:'12px', fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase', cursor:'pointer', fontFamily:"'Inter',sans-serif", whiteSpace:'nowrap' }}>
                    {temRascunho(proximoTreino.id) ? (lang==='pt'?'Retomar treino':'Resume workout') : tx.iniciarTreino} →
                  </button>
                </div>
              )}

              {!checkinAtivo && treinosComplementares.length > 0 && (
                <div style={{ padding:'12px 14px', marginBottom:14, borderRadius:18, background:'rgba(137,91,64,.07)', border:'1px solid rgba(137,91,64,.12)', fontSize:12, color:'var(--text-3)', lineHeight:1.55 }}>
                  <strong style={{ display:'block', color:'var(--text)', marginBottom:3 }}>{lang==='pt' ? 'Como sua ficha está organizada' : 'How your plan is organized'}</strong>
                  {lang==='pt' ? 'Os treinos com letras são as sessões principais. O bloco complementar é mais curto e só deve ser encaixado nos dias orientados pela equipe.' : 'Lettered workouts are your main sessions. The complementary block is shorter and should only be added on team-recommended days.'}
                </div>
              )}

              {treinosOrdenados.map(treino => {
                const ativo = checkinAtivo===treino.id;
                const tot = totalEx(treino.id);
                const conc = ativo ? concluidos() : 0;
                const todosConcluidos = ativo && conc===tot && tot>0;

                return (
                  <div key={treino.id} className="treino-card" style={{ border: ativo?'1px solid rgba(137,91,64,.24)':'1px solid rgba(255,255,255,.72)', padding: ativo ? '18px' : '0', overflow:'hidden' }}>
                    {/* Header clicável quando não ativo */}
                    {!ativo && (
                      <div className="workout-card-head" onClick={() => setTreinoExpandido(t => t === treino.id ? null : treino.id)}
                        style={{ padding:'16px 20px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px' }}>
                        <div>
                          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'2px' }}>
                            <div style={{ fontFamily:"'Inter', sans-serif", fontSize:'16px', fontWeight:700, color:'var(--text)' }}>{treino.titulo}</div>
                            {proximoTreino?.id===treino.id && <span style={{ fontSize:'10px', fontWeight:700, padding:'3px 8px', background:'rgba(137,91,64,0.1)', color:'var(--accent)', borderRadius:'100px', letterSpacing:'0.06em', textTransform:'uppercase' }}>{lang==='pt'?'Faça este agora':'Do this next'}</span>}
                            {treinoComplementar(treino) && <span style={{ fontSize:'10px', fontWeight:700, padding:'3px 8px', background:'rgba(46,125,50,.08)', color:'#2E7D32', borderRadius:'100px' }}>{lang==='pt'?'Complementar':'Complement'}</span>}
                          </div>
                          <div style={{ fontSize:'12px', color:'var(--text-2)', lineHeight:1.38 }}>{treino.descricao} · cerca de {duracaoEstimada(treino)} min · {treino.exercicios?.length || 0} {lang==='pt'?'exercícios':'exercises'}</div>
                        </div>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-2)" strokeWidth="1.5" strokeLinecap="round">
                          <polyline points={treinoExpandido===treino.id ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
                        </svg>
                      </div>
                    )}
                    {/* Conteúdo expandido ou treino ativo */}
                    {(ativo || treinoExpandido===treino.id) && (
                    <div style={{ padding: ativo ? '0' : '0 20px 20px' }}>
                    {ativo && <div style={{ marginBottom:'14px' }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'2px' }}>
                        <div style={{ fontFamily:"'Inter', sans-serif", fontSize:'18px', fontWeight:700, color:'var(--text)' }}>{treino.titulo}</div>
                        {proximoTreino?.id===treino.id && meusCheckins.length>0 && <span style={{ fontSize:'10px', fontWeight:700, padding:'3px 8px', background:'rgba(137,91,64,0.1)', color:'var(--accent)', borderRadius:'100px', letterSpacing:'0.06em', textTransform:'uppercase' }}>{tx.sugerido}</span>}
                      </div>
                      <div style={{ fontSize:'12px', color:'var(--text-2)', lineHeight:1.4 }}>{treino.descricao} · {lang==='pt'?'Semana':'Week'} {treino.semana}</div>
                      {treino.obs && <ObsColapsavel obs={treino.obs} label={tx.daEquipe} id={'a-'+treino.id} marginTop='10px' />}
                    </div>}
                    {!ativo && treino.obs && <ObsColapsavel obs={treino.obs} label={tx.daEquipe} id={'e-'+treino.id} marginBottom='12px' />}

                    {ativo && (
                      <div style={{ marginBottom:'14px' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'5px' }}>
                          <div style={{ fontSize:'11px', color:'var(--accent)', fontWeight:600 }}>{conc} {lang==='pt'?'de':'of'} {tot} {lang==='pt'?'exercícios':'exercises'}</div>
                          <div style={{ fontSize:'11px', color:'var(--text-2)' }}>{Math.round((conc/tot)*100)}%</div>
                        </div>
                        <div style={{ height:'4px', background:'var(--divider)', borderRadius:'6px' }}>
                          <div style={{ height:'100%', width:`${(conc/tot)*100}%`, background:'var(--accent)', borderRadius:'6px', transition:'width 0.3s' }} />
                        </div>
                      </div>
                    )}

                    {ativo && (
                      <button type="button" onClick={() => setFotoTreino(treino)} style={{ width:'100%', marginBottom:14, padding:'11px 14px', border:'1px solid rgba(137,91,64,.18)', borderRadius:18, background:'rgba(137,91,64,.06)', color:'var(--accent)', fontSize:13, fontWeight:650, cursor:'pointer' }}>
                        📸 {lang==='pt'?'Registrar um momento':'Capture a moment'}
                      </button>
                    )}

                    <div className="section-lbl">{ativo ? (lang==='pt'?'Exercícios — toque para expandir':'Exercises — tap to expand') : tx.exercicios} <div className="section-line" /></div>

                    {treino.exercicios?.map((ex, exIdx) => {
                      const ultimaCarga = ultimaCargaExercicio(ex.nome);
                      const combinacao = dadosCombinacao(ex);
                      const anterior = exIdx > 0 ? dadosCombinacao(treino.exercicios[exIdx - 1]) : null;
                      const proximo = exIdx < treino.exercicios.length - 1 ? dadosCombinacao(treino.exercicios[exIdx + 1]) : null;
                      const abreGrupo = combinacao && (!anterior || anterior.id !== combinacao.id);
                      const fechaGrupo = combinacao && (!proximo || proximo.id !== combinacao.id);
                      return ativo ? (
                        <React.Fragment key={ex.id}>
                        {abreGrupo && <div style={{ margin:'4px 0 0', padding:'10px 14px 8px', border:'1.5px solid rgba(137,91,64,0.28)', borderBottom:0, borderRadius:'22px 22px 0 0', background:'rgba(137,91,64,0.055)' }}>
                          <div style={{ fontSize:11, fontWeight:750, letterSpacing:'0.09em', textTransform:'uppercase', color:'var(--accent)' }}>{combinacao.tipo}</div>
                          <div style={{ marginTop:3, fontSize:11, color:'var(--text-2)', lineHeight:1.4 }}>{lang==='pt' ? 'Faça os exercícios em sequência. Descanse somente depois do último.' : 'Perform the exercises in sequence. Rest only after the last one.'}</div>
                        </div>}
                        <div ref={el => exercicioRefs.current[ex.id] = el} style={combinacao ? { padding:'0 8px', borderLeft:'1.5px solid rgba(137,91,64,0.28)', borderRight:'1.5px solid rgba(137,91,64,0.28)', background:'rgba(137,91,64,0.025)' } : undefined}>
                          <ExercicioItem ex={ex} checkinAtivo={ativo} exState={checkinData[ex.id]} lang={lang}
                            ultimaCarga={ultimaCarga}
                            semDescansoEntre={Boolean(combinacao && !fechaGrupo)}
                            onUpdate={(field,val) => setCheckinData(d => ({...d,[ex.id]:{...d[ex.id],[field]:val}}))}
                            onConcluir={() => {
                              setCheckinData(d => ({...d,[ex.id]:{...d[ex.id],concluido:true,marcando:false}}));
                              setTimeout(() => scrollProximo(treino.id), 300);
                            }}
                          />
                        </div>
                        {fechaGrupo && <div style={{ height:10, marginBottom:10, border:'1.5px solid rgba(137,91,64,0.28)', borderTop:0, borderRadius:'0 0 22px 22px', background:'rgba(137,91,64,0.025)' }} />}
                        </React.Fragment>
                      ) : (
                        <div key={ex.id} className="workout-preview-ex" style={{ marginBottom:'10px' }}>
                          <div style={{ width:'44px', height:'44px', borderRadius:'18px', background:'#e8d5c7', color:'#895b40', display:'grid', placeItems:'center', flexShrink:0 }}>
                            {React.cloneElement(LINE_ICONS.treino, { style:{ width:'20px', height:'20px', stroke:'currentColor', strokeWidth:2, fill:'none' } })}
                          </div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:'14px', fontWeight:650, color:'var(--text)', lineHeight:1.28 }}>{ex.nome}</div>
                            <div style={{ fontSize:'12px', color:'var(--text-2)', marginTop:'3px', lineHeight:1.35 }}>{ex.series || 1} {lang==='pt'?'séries':'sets'}{ex.reps?` · ${String(ex.reps).replace(/^\s*\d+\s*x\s*/i,'')}`:''}{ex.carga?` · ${ex.carga}`:''}</div>
                          </div>
                          {ultimaCargaExercicio(ex.nome) && (
                            <div className="last-load" style={{ fontSize:'10px', color:'var(--accent)', fontWeight:600, background:'var(--accent-light, rgba(137,91,64,0.08))', padding:'4px 8px', borderRadius:'100px' }}>
                              ↩ {ultimaCargaExercicio(ex.nome)}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    <div style={{ marginTop:'14px' }}>
                      {!ativo ? (
                        <button onClick={() => iniciarCheckin(treino)} style={{ width:'100%', padding:'12px', background:'var(--text)', color:'#FAF7F2', border:'none', borderRadius:'20px', fontSize:'13px', fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase', cursor:'pointer', fontFamily:"'Inter',sans-serif" }}>
                          {temRascunho(treino.id) ? (lang==='pt'?'Retomar treino':'Resume workout') : tx.iniciarTreino}
                        </button>
                      ) : (
                        <div style={{ display:'flex', gap:'8px' }}>
                          <button onClick={() => pausarCheckin(treino.id)} style={{ flex:1, padding:'11px', background:'transparent', border:'1.5px solid var(--divider)', color:'var(--text-2)', borderRadius:'20px', fontSize:'12px', cursor:'pointer', fontFamily:"'Inter',sans-serif" }}>{tx.pausar}</button>
                          <button onClick={() => pedirNotaFinal(treino)} disabled={!todosConcluidos} style={{ flex:2, padding:'11px', background: todosConcluidos?'#2E7D32':'rgba(37,28,25,0.12)', color: todosConcluidos?'#fff':'var(--text-2)', border:'none', borderRadius:'20px', fontSize:'13px', fontWeight:600, letterSpacing:'0.04em', textTransform:'uppercase', cursor: todosConcluidos?'pointer':'not-allowed', fontFamily:"'Inter',sans-serif" }}>
                            {todosConcluidos ? tx.finalizarTreino : tx.restantes(tot-conc)}
                          </button>
                        </div>
                      )}
                    </div>
                    </div>
                    )}
                  </div>
                );
              })}
            </>
        )}

        {/* DIETA */}
        {tab==='dieta' && (
          user?.acessoDieta === false
            ? <div className="empty-state" style={{ padding:'28px 22px', background:'rgba(255,255,255,.58)', border:'1px solid rgba(216,202,182,.45)' }}>
                <div style={{ width:42, height:42, display:'grid', placeItems:'center', margin:'0 auto 10px', borderRadius:18, background:'rgba(137,91,64,.07)', color:'var(--accent)', fontSize:18 }}>◇</div>
                <strong style={{ display:'block', color:'var(--text)', fontSize:15, marginBottom:6 }}>{lang==='pt'?'Nutrição não está incluída no seu pacote atual':'Nutrition is not included in your current package'}</strong>
                <span style={{ display:'block', maxWidth:310, margin:'0 auto', lineHeight:1.55 }}>{lang==='pt'?'Seu acesso ao treino continua normalmente. Se quiser conhecer o acompanhamento nutricional, fale com a equipe quando desejar.':'Your workout access continues as usual. If you would like to learn about nutrition support, talk to the team whenever you wish.'}</span>
                <button type="button" onClick={() => navigate('/aluna/chat')} style={{ marginTop:14, padding:'9px 15px', border:'1px solid rgba(137,91,64,.18)', borderRadius:999, background:'transparent', color:'var(--accent)', fontSize:12, fontWeight:650 }}>{lang==='pt'?'Conhecer a opção de Nutrição':'Learn about Nutrition'}</button>
              </div>
            : nutricaoView
            ? <NutricaoFullScreen titulo={nutricaoTiles.find(t => t.key===nutricaoView)?.label} onBack={() => setNutricaoView(null)}>
                {nutricaoView==='compras' && (
                  <div style={{ background:'var(--surface, #fff)', borderRadius:'20px', padding:'4px 16px', boxShadow:'var(--sh-card, var(--sh-card))' }}>
                    {listaComprasPrincipal.map((nome,i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 0', borderTop: i > 0 ? '1px solid rgba(37,28,25,0.05)' : 'none' }}>
                        <div style={{ width:'20px', height:'20px', borderRadius:'6px', border:'1.5px solid var(--divider)', flexShrink:0 }} />
                        <span style={{ fontSize:'14px', color:'var(--text)', fontWeight:500 }}>{nome}</span>
                      </div>
                    ))}
                  </div>
                )}
                {nutricaoView==='observacoes' && (
                  <>
                    {orientacoesPrincipal.length > 0 && (
                      <div style={{ marginBottom: dietaPrincipal?.obs ? '18px' : 0, background:'var(--surface, #fff)', borderRadius:'20px', padding:'6px 16px', boxShadow:'var(--sh-card, var(--sh-card))' }}>
                        {orientacoesPrincipal.map((o,i) => (
                          <div key={i} style={{ display:'flex', gap:'10px', padding:'9px 0', borderTop: i > 0 ? '1px solid rgba(37,28,25,0.05)' : 'none' }}>
                            <span style={{ color:'var(--accent)', fontWeight:700, flexShrink:0 }}>•</span>
                            <span style={{ fontSize:'13px', color:'var(--text)', lineHeight:1.5 }}>{o}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {dietaPrincipal?.obs && (
                      <div style={{ padding:'14px 16px', background:'rgba(137,91,64,0.06)', borderLeft:'2px solid var(--accent)', borderRadius:'0 12px 12px 0', fontSize:'13px', color:'var(--text-3)', lineHeight:1.7 }}>
                        <strong style={{ color:'var(--accent)', display:'block', marginBottom:'6px' }}>{tx.daBarbara}</strong>
                        {dietaPrincipal.obs}
                      </div>
                    )}
                  </>
                )}
                {nutricaoView==='receitas' && (
                  <>
                    <div style={{ marginBottom:'16px' }}>
                      <BaixarPdfBtn label={lang==='pt'?'Baixar PDF':'Download PDF'} onClick={() => gerarPdfReceitas(receitasPrincipal, dietaPrincipal?.titulo, user?.name)} />
                    </div>
                    {receitasPrincipal.map((r,i) => (
                      <div key={i} style={{ marginBottom:'12px', background:'var(--surface, #fff)', borderRadius:'20px', padding:'14px 16px', boxShadow:'var(--sh-card, var(--sh-card))' }}>
                        <div style={{ fontSize:'11px', fontWeight:700, color:'var(--accent)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'6px' }}>{r.refeicao}{r.opcao ? ` · ${r.opcao}` : ''}</div>
                        <div style={{ fontSize:'13px', color:'var(--text)', lineHeight:1.6, whiteSpace:'pre-line' }}>{r.receita}</div>
                      </div>
                    ))}
                  </>
                )}
                {nutricaoView==='suplementacao' && (
                  <div style={{ background:'var(--surface, #fff)', borderRadius:'20px', padding:'4px 14px', boxShadow:'var(--sh-card, var(--sh-card))' }}>
                    {dietaPrincipal?.suplementacao.map((s,i) => (
                      <div key={i} style={{ padding:'10px 0', borderTop: i > 0 ? '1px solid rgba(37,28,25,0.05)' : 'none' }}>
                        <span style={{ fontSize:'13px', color:'var(--text)', fontWeight:600 }}>{s.nome}</span>
                        {s.dose && <span style={{ fontSize:'12px', color:'var(--text-2)' }}> · {s.dose}</span>}
                        {s.momento && <div style={{ fontSize:'11px', color:'var(--text-3)', marginTop:'2px' }}>{s.momento}</div>}
                      </div>
                    ))}
                  </div>
                )}
                {nutricaoView==='exames' && meusExames.map(exame => (
                  <div key={exame.id} className="treino-card">
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'10px', marginBottom:'4px' }}>
                      <div style={{ fontFamily:"'Inter', sans-serif", fontSize:'18px', fontWeight:700, color:'var(--text)' }}>🧪 {exame.titulo}</div>
                      <BaixarPdfBtn label={lang==='pt'?'PDF':'PDF'} onClick={() => gerarPdfExame(exame, user?.name)} />
                    </div>
                    {exame.obs && <div style={{ padding:'9px 12px', background:'rgba(137,91,64,0.06)', borderLeft:'2px solid var(--accent)', borderRadius:'0 6px 6px 0', fontSize:'12px', color:'var(--text-3)', marginTop:'12px', marginBottom:'16px', lineHeight:1.6 }}><strong style={{ color:'var(--accent)' }}>{tx.daBarbara}</strong> {exame.obs}</div>}

                    {exame.categorias?.length > 0 && (
                      <div style={{ marginBottom:'18px' }}>
                        <div className="section-lbl">{lang==='pt'?'Pedido de exame':'Lab order'} <div className="section-line" /></div>
                        {exame.categorias.map((cat, ci) => (
                          <div key={ci} style={{ marginBottom:'12px', background:'var(--surface, #fff)', borderRadius:'20px', padding:'14px 16px', boxShadow:'var(--sh-card, var(--sh-card))' }}>
                            {cat.titulo && <div style={{ fontSize:'11px', fontWeight:700, color:'var(--accent)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'10px' }}>{cat.titulo}</div>}
                            <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
                              {cat.exames.map((nome,i) => (
                                <span key={i} style={{ fontSize:'12px', fontWeight:500, color:'var(--text)', background:'var(--surface-2)', padding:'6px 12px', borderRadius:'100px' }}>{nome}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {exame.orientacoesColeta && (
                      <div style={{ padding:'12px 14px', background:'rgba(41,121,182,0.06)', borderLeft:'2px solid #2979B6', borderRadius:'0 6px 6px 0', marginBottom:'16px' }}>
                        <div style={{ fontSize:'11px', fontWeight:700, color:'#2979B6', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' }}>{lang==='pt'?'Orientações para coleta':'Collection guidelines'}</div>
                        <div style={{ fontSize:'12px', color:'var(--text-3)', lineHeight:1.6, whiteSpace:'pre-line' }}>{exame.orientacoesColeta}</div>
                      </div>
                    )}

                    <div className="section-lbl">{lang==='pt'?'Resultado':'Result'} <div className="section-line" /></div>
                    <ExameResultados exame={exame} alunaId={user?.id} canUpload={!previewMode} />
                  </div>
                ))}
              </NutricaoFullScreen>
            : (minhasDietas.length===0 && meusExames.length===0)
            ? <div className="empty-state" style={{ padding:'26px 20px' }}>
                <div style={{ fontSize:28, marginBottom:8 }}>🍽️</div>
                <strong style={{ display:'block', color:'var(--text)', fontSize:15, marginBottom:6 }}>{lang==='pt'?'Seu plano alimentar está sendo preparado':'Your meal plan is being prepared'}</strong>
                <span style={{ display:'block', lineHeight:1.55, marginBottom:14 }}>{lang==='pt'?'Quando a Bárbara publicar o plano, ele aparecerá aqui com refeições, hidratação, lista de compras e orientações.':'When Bárbara publishes your plan, it will appear here with meals, hydration, a shopping list and guidance.'}</span>
                <button type="button" onClick={() => navigate('/aluna/chat')} style={{ padding:'10px 16px', border:0, borderRadius:999, background:'var(--text)', color:'#fff9f2', fontSize:12, fontWeight:650 }}>{lang==='pt'?'Tirar uma dúvida no chat →':'Ask a question in chat →'}</button>
              </div>
            : <>
            {dietaPrincipal && (
              <div className="nutrition-summary">
                <div className="nutrition-chip">
                  <small>{lang === 'pt' ? 'Próxima refeição' : 'Next meal'}</small>
                  <strong>{refeicaoAtualPrincipal?.nome || (lang === 'pt' ? 'Cardápio' : 'Meal plan')}</strong>
                </div>
                <div className="nutrition-chip">
                  <small>{lang === 'pt' ? 'Hidratação' : 'Hydration'}</small>
                  <strong>{dietaPrincipal?.agua?.meta || (lang === 'pt' ? 'Meta diária' : 'Daily goal')}</strong>
                </div>
              </div>
            )}
            {nutricaoTemAlgumAtivo && (
              <div className="nutrition-actions">
                {nutricaoTiles.filter(tile => tile.ativo).map(tile => (
                  <button
                    key={tile.key}
                    className="nutrition-action"
                    onClick={() => setNutricaoView(tile.key)}
                  >
                    <i>{React.cloneElement(NUTRICAO_ICONS[tile.key], { width: 20, height: 20, fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' })}</i>
                    <span>
                      <strong>{tile.label}</strong>
                      <small>{lang === 'pt' ? 'Ver detalhes' : 'See details'}</small>
                    </span>
                    <em>›</em>
                  </button>
                ))}
              </div>
            )}
            {dietaPrincipal && <PlanoAlimentarPreview dieta={dietaPrincipal} lang={lang} alunaNome={user?.name} />}

            {dietasAnteriores.length > 0 && (
              <div style={{ marginTop:'8px' }}>
                <button onClick={() => setMostrarDietasAnteriores(v => !v)} style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', background:'var(--surface, #fff)', border:'none', borderRadius:'20px', boxShadow:'var(--sh-card, var(--sh-card))', fontSize:'12px', fontWeight:600, color:'var(--text-2)', cursor:'pointer', fontFamily:"'Inter',sans-serif" }}>
                  <span>{lang==='pt'?'Planos anteriores':'Previous plans'} ({dietasAnteriores.length})</span>
                  <span style={{ transform: mostrarDietasAnteriores ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}>▾</span>
                </button>
                {mostrarDietasAnteriores && (
                  <div style={{ marginTop:'8px', display:'flex', flexDirection:'column', gap:'8px' }}>
                    {dietasAnteriores.map(dieta => (
                      <div key={dieta.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'10px', padding:'12px 16px', background:'var(--surface, #fff)', borderRadius:'16px', boxShadow:'var(--sh-card, var(--sh-card))' }}>
                        <div>
                          <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text)' }}>{dieta.titulo}</div>
                          {dieta.criadoEm && <div style={{ fontSize:'11px', color:'var(--text-2)', marginTop:'2px' }}>{lang==='pt'?'Cadastrado em':'Registered on'} {formatDate(dieta.criadoEm)}</div>}
                        </div>
                        <BaixarPdfBtn label={lang==='pt'?'PDF':'PDF'} onClick={() => gerarPdfPlanoAlimentar(dieta, normalizarRefeicoes(dieta.refeicoes), user?.name)} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            </>
        )}

        {/* HISTÓRICO */}
        {tab==='historico' && (
          meusCheckins.length===0
            ? <div className="empty-state">{tx.noCheckin}</div>
            : <HistoricoColapsavel checkins={meusCheckins} treinos={meusTreinos} lang={lang} formatDate={formatDate} />
        )}

        {/* MENSAGENS */}
        {tab==='mensagens' && (
          <section className="aluna-chat">
            <div className="aluna-chat-hero">
              <div className="aluna-chat-avatar">TC</div>
              <div>
                <span>Team Carol Macedo</span>
                <strong>{lang==='pt'?'Canal direto com a equipe':'Direct channel with the team'}</strong>
                <p>{lang==='pt'?'Use para dúvidas do plano, ajustes de rotina e acompanhamento.':'Use it for plan questions, routine adjustments and follow-up.'}</p>
              </div>
            </div>

            <div className="aluna-chat-thread">
              {mensagens.length === 0 ? (
                <div className="aluna-chat-empty">
                  {lang==='pt'?'Este é seu canal com a equipe. Envie sua primeira dúvida sobre exercícios, dor, aparelhos ou ajustes no plano.':'This is your team channel. Send your first question about exercises, pain, equipment or plan adjustments.'}
                </div>
              ) : mensagens.map(msg => {
                const isAluna = msg.remetente === 'aluna';
                return (
                  <div key={msg.id} className={`aluna-message ${isAluna ? 'is-user' : 'is-team'}`}>
                    {!isAluna && <small>Team Carol</small>}
                    <p>{msg.texto}</p>
                    <time>{formatMsgHora(msg.created_at)}</time>
                  </div>
                );
              })}
              <div ref={msgBottomRef} />
            </div>

            {!previewMode && <form className="aluna-chat-composer" onSubmit={enviarMensagem}>
              <textarea value={msgTexto} onChange={e => setMsgTexto(e.target.value)}
                onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); enviarMensagem(e); } }}
                placeholder={lang==='pt'?'Escreva uma mensagem...':'Write a message...'}
                rows={1} />
              <button type="submit" disabled={!msgTexto.trim() || msgEnviando}>{msgEnviando ? '...' : 'Enviar'}</button>
            </form>}
          </section>
        )}

        {/* AVALIAÇÃO */}
        {tab==='avaliacao' && (
          <div className="treino-card">
            <div style={{ fontFamily:"'Inter', sans-serif", fontSize:'18px', fontWeight:700, color:'var(--text)', marginBottom:'6px' }}>{lang==='pt'?'Avaliação física':'Physical Assessment'}</div>
            <div style={{ fontSize:'13px', color:'var(--text-2)', marginBottom:'20px', lineHeight:1.6 }}>{lang==='pt'?'Agende sua avaliação com o Team Carol Macedo.':'Schedule your assessment with Team Carol Macedo.'}</div>
            <button disabled={previewMode} onClick={() => navigate('/aluna/agendar')} style={{ width:'100%', padding:'12px', background:'var(--accent)', color:'var(--btn-text)', border:'none', borderRadius:'20px', fontSize:'13px', fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase', cursor:previewMode?'not-allowed':'pointer', opacity:previewMode?.55:1, fontFamily:"'Inter',sans-serif" }}>
              {tx.agendarAvaliacao}
            </button>
          </div>
        )}
      </div>

      {!previewMode && <AlunaTabBar current={tab === 'treinos' ? 'treino' : tab === 'dieta' ? 'dieta' : tab === 'avaliacao' ? 'agendar' : tab === 'mensagens' ? 'chat' : undefined} />}
    </div>
  );
}
